import React, { useEffect, useRef, useState } from 'react';
import { Box, FormHelperText } from '@mui/material';

// Chave reCAPTCHA Enterprise do tipo caixa ("Não sou um robô"); o login do GovHub (applications) usa outra, por pontuação.
// REACT_APP_RECAPTCHA_SITE_KEY troca a chave no build
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LexgPMtAAAAALj3EknV7fKtPfoH6bojPbys0jXt';

const ONLOAD_CALLBACK = 'onRecaptchaEnterpriseLoad';
const SCRIPT_URL = `https://www.google.com/recaptcha/enterprise.js?onload=${ONLOAD_CALLBACK}&render=explicit&hl=pt-BR`;

interface RecaptchaEnterprise {
  render(container: HTMLElement, parameters: {
    sitekey: string;
    action?: string;
    callback?: (token: string) => void;
    'expired-callback'?: () => void;
    'error-callback'?: () => void;
  }): number;
  reset(widgetId?: number): void;
}

declare global {
  interface Window {
    grecaptcha?: { enterprise: RecaptchaEnterprise };
    onRecaptchaEnterpriseLoad?: () => void;
  }
}

let scriptLoading: Promise<RecaptchaEnterprise> | undefined;

// Carrega o enterprise.js uma única vez por página; se falhar (rede, bloqueador), a próxima montagem tenta de novo
function loadRecaptchaEnterprise(): Promise<RecaptchaEnterprise> {
  if (!scriptLoading) {
    scriptLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      window[ONLOAD_CALLBACK] = () => resolve(window.grecaptcha!.enterprise);
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        scriptLoading = undefined;
        script.remove();
        reject(new Error('Falha ao carregar o reCAPTCHA'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptLoading;
}

interface RecaptchaFieldProps {
  /** Ação registrada na avaliação do Google (letras, números, "_" e "/"), ex.: CONTACT */
  action: string;
  token: string;
  onChange: (token: string) => void;
  error?: string;
}

/**
 * reCAPTCHA Enterprise com caixa ("Não sou um robô"). O token vale uma única validação: quando volta a ficar vazio
 * (enviado, expirado ou com erro) o widget é desmarcado para o usuário marcar de novo.
 */
export default function RecaptchaField({ action, token, onChange, error }: RecaptchaFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<{ api: RecaptchaEnterprise; id: number }>();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let disposed = false;
    // Elemento novo a cada montagem: o Google não renderiza duas vezes no mesmo elemento (StrictMode monta duas vezes)
    const host = document.createElement('div');
    containerRef.current?.appendChild(host);

    loadRecaptchaEnterprise()
      .then((api) => {
        if (disposed) return;
        setUnavailable(false);
        const id = api.render(host, {
          sitekey: RECAPTCHA_SITE_KEY,
          action,
          callback: (value) => onChangeRef.current(value),
          'expired-callback': () => onChangeRef.current(''),
          'error-callback': () => onChangeRef.current(''),
        });
        widgetRef.current = { api, id };
      })
      .catch(() => {
        if (!disposed) setUnavailable(true);
      });

    return () => {
      disposed = true;
      widgetRef.current = undefined;
      host.remove();
      onChangeRef.current('');
    };
  }, [action]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!token && widget) widget.api.reset(widget.id);
  }, [token]);

  return (
    <Box>
      <Box
        ref={containerRef}
        sx={{
          // O widget do Google tem largura fixa (304px); em telas muito estreitas reduz para caber no formulário
          '@media (max-width: 359px)': { transform: 'scale(0.86)', transformOrigin: 'center' },
        }}
      />
      {unavailable && (
        <FormHelperText error>
          Não foi possível carregar a verificação "Não sou um robô". Verifique a conexão e recarregue a página.
        </FormHelperText>
      )}
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
}
